import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../services/api';
import { Bus, Clock, Play, Square, Users, Activity } from 'lucide-react';

export const MonitoringStatusPanel: React.FC = () => {
  const [statuses, setStatuses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchMonitoringStatuses();
  }, []);

  const fetchMonitoringStatuses = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllMonitoringStatus();
      if (response.status === 'success') {
        setStatuses(response.statuses);
      } else {
        toast.error(response.message || 'Failed to fetch monitoring statuses');
      }
    } catch (error) {
      console.error('Error fetching monitoring statuses:', error);
      toast.error('Failed to fetch monitoring statuses');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (isRunning: boolean) => {
    return isRunning ? (
      <Play className="h-5 w-5 text-green-500" />
    ) : (
      <Square className="h-5 w-5 text-gray-400" />
    );
  };

  const getStatusColor = (isRunning: boolean) => {
    return isRunning 
      ? 'bg-green-50 border-green-200 text-green-800' 
      : 'bg-gray-50 border-gray-200 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading monitoring statuses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Monitoring Status</h2>
              <p className="text-sm text-gray-600">All users' bus monitoring status</p>
            </div>
          </div>
          <button
            onClick={fetchMonitoringStatuses}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <Clock className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {statuses.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Monitoring Activity</h3>
            <p className="text-gray-600">No users are currently monitoring bus availability.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {statuses.map((status, index) => (
              <div
                key={index}
                className={`rounded-lg border p-4 ${getStatusColor(status.is_running)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(status.is_running)}
                    <div>
                      <h4 className="font-medium text-gray-900">{status.email}</h4>
                      <p className="text-sm text-gray-600">
                        {status.is_running ? 'Monitoring active' : 'Monitoring inactive'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      {status.is_running ? 'Started' : 'Last updated'}: {formatDate(status.started_at || status.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Bus className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="text-sm font-medium text-blue-800">Summary</h4>
              <p className="text-sm text-blue-700">
                {statuses.filter(s => s.is_running).length} active monitoring sessions out of {statuses.length} total users
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 